/**
 * DataValuesEditableTable - Editable table for entering metric data values
 *
 * Features:
 * - First column (dates) is read-only
 * - Second column (values) is editable
 * - Keyboard navigation: Tab, Enter, Arrow keys
 * - Copy/paste support for values
 * - New row adds next month/quarter/week automatically
 * - Supports monthly, quarterly, and weekly cadences
 *
 * A row's period is (period_year, period_month) for monthly/quarterly cards
 * or (period_year, period_week) for weekly cards - never both, mirroring the
 * backend's DataValue storage convention (see models.py). Internally the
 * arithmetic (stepping to the next/previous period, sorting) treats
 * whichever of the two is populated as an opaque "unit" number, since a
 * (year, unit) tuple sorts and steps the same way regardless of what the
 * unit means.
 */
import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { TrashIcon, WarningIcon, PlusIcon } from '@phosphor-icons/react';
import Button from 'src/components/ui/Button';
import type { DataValueType, LevelCalculationMode, UpdateCadence } from '../api/types';
import { MONTHS } from '../utils/constants';
import { validateDataValueInput, type ValidationError } from '../utils/validation';
import { getIsoWeek, addIsoWeeks, formatIsoWeekFullLabel, londonToday } from '../utils/isoWeek';

interface PeriodValue {
  period_year: number;
  period_month: number | null;
  period_week: number | null;
}

interface DataValueRow {
  id: string;
  savedValueId: string | null; // Original saved value ID for deletion
  period_year: number;
  period_month: number | null;
  period_week: number | null;
  value: string;
  isPending: boolean;
  isNew?: boolean;
}

interface DataValuesEditableTableProps {
  /** Existing saved data values */
  savedValues: Array<{
    id: string;
    period_year: number;
    period_month: number | null;
    period_week: number | null;
    value: number;
  }>;
  /** Pending (unsaved) values - value can be null to represent empty/no value */
  pendingValues: Array<{
    period_year: number;
    period_month: number | null;
    period_week: number | null;
    value: number | null;
  }>;
  /** IDs of saved values marked for deletion */
  deletedSavedValueIds: string[];
  /** Update cadence (monthly, quarterly, or weekly) */
  updateCadence: UpdateCadence;
  /** Data value type for validation */
  dataValueType: DataValueType;
  /** Calculation mode — used to relax the 0-100 cap for deviation modes */
  levelCalculationMode?: LevelCalculationMode | null;
  /** When true, render a Yes / No / Empty toggle instead of a numeric input */
  isStatusCard?: boolean;
  /** Callback when pending values change */
  onPendingValuesChange: (
    values: Array<{
      period_year: number;
      period_month: number | null;
      period_week: number | null;
      value: number | null;
    }>
  ) => void;
  /** Callback when a saved value is deleted */
  onDeleteSavedValue: (id: string) => void;
  /** Read-only mode */
  readOnly?: boolean;
  /** Fill the parent and scroll only the table body (card details pane). */
  fillHeight?: boolean;
  /**
   * The dashboard's selected reporting period. When set, that row is marked
   * as "This period" so a past month/week is still findable in the list.
   */
  highlightedPeriod?: PeriodValue | null;
}

// Fiscal year quarters (April = start of fiscal year)
// Q1 = Apr-Jun (rep month 6), Q2 = Jul-Sep (rep month 9), Q3 = Oct-Dec (rep month 12), Q4 = Jan-Mar (rep month 3)
const QUARTERS = [
  { value: 1, label: 'Q1', representativeMonth: 6 },
  { value: 2, label: 'Q2', representativeMonth: 9 },
  { value: 3, label: 'Q3', representativeMonth: 12 },
  { value: 4, label: 'Q4', representativeMonth: 3 }
];

const getQuarterFromMonth = (month: number): number => Math.floor(((month + 8) % 12) / 3) + 1;
const getRepresentativeMonth = (quarter: number): number => {
  const mapping: Record<number, number> = { 1: 6, 2: 9, 3: 12, 4: 3 };
  return mapping[quarter] || 6;
};

/** Extract the cadence's "unit" (month, or ISO week) from a period value. */
function getUnit(period: { period_month: number | null; period_week: number | null }, cadence: UpdateCadence): number {
  return cadence === 'weekly' ? (period.period_week as number) : (period.period_month as number);
}

/** Build a period value object from (year, unit) for the given cadence. */
function fromUnit(year: number, unit: number, cadence: UpdateCadence): PeriodValue {
  if (cadence === 'weekly') {
    return { period_year: year, period_month: null, period_week: unit };
  }
  return { period_year: year, period_month: unit, period_week: null };
}

function isSamePeriod(row: PeriodValue, other: PeriodValue, cadence: UpdateCadence): boolean {
  return row.period_year === other.period_year && getUnit(row, cadence) === getUnit(other, cadence);
}

function stepQuarter(year: number, month: number, direction: 1 | -1): { year: number; month: number } {
  const currentQuarter = getQuarterFromMonth(month);
  if (direction === 1) {
    const nextQuarter = currentQuarter === 4 ? 1 : currentQuarter + 1;
    return { year: nextQuarter === 4 ? year + 1 : year, month: getRepresentativeMonth(nextQuarter) };
  }
  const prevQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1;
  return { year: currentQuarter === 4 ? year - 1 : year, month: getRepresentativeMonth(prevQuarter) };
}

function stepMonth(year: number, month: number, direction: 1 | -1): { year: number; month: number } {
  let m = month + direction;
  let y = year;
  if (m > 12) {
    m = 1;
    y++;
  }
  if (m < 1) {
    m = 12;
    y--;
  }
  return { year: y, month: m };
}

/** Advance (or go back) one period for the given cadence. */
function stepPeriod(
  year: number,
  unit: number,
  cadence: UpdateCadence,
  direction: 1 | -1
): { year: number; unit: number } {
  if (cadence === 'weekly') {
    const { isoYear, isoWeek } = addIsoWeeks(year, unit, direction);
    return { year: isoYear, unit: isoWeek };
  }
  if (cadence === 'quarterly') {
    const { year: y, month } = stepQuarter(year, unit, direction);
    return { year: y, unit: month };
  }
  const { year: y, month } = stepMonth(year, unit, direction);
  return { year: y, unit: month };
}

/** The current period ("now") for the given cadence. */
function currentPeriod(cadence: UpdateCadence): { year: number; unit: number } {
  if (cadence === 'weekly') {
    // Matches the backend's Europe/London "current week" convention (see
    // ReportingPeriod.current_week_london() in dashboards/domain.py) rather
    // than the viewer's local date, which would drift near midnight or
    // across DST.
    const { isoYear, isoWeek } = getIsoWeek(londonToday());
    return { year: isoYear, unit: isoWeek };
  }
  const now = new Date();
  let month = now.getMonth() + 1;
  if (cadence === 'quarterly') {
    month = getRepresentativeMonth(getQuarterFromMonth(month));
  }
  return { year: now.getFullYear(), unit: month };
}

export default function DataValuesEditableTable({
  savedValues,
  pendingValues,
  deletedSavedValueIds,
  updateCadence,
  dataValueType,
  levelCalculationMode,
  isStatusCard = false,
  onPendingValuesChange,
  onDeleteSavedValue,
  readOnly = false,
  fillHeight = false,
  highlightedPeriod = null
}: DataValuesEditableTableProps) {
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const highlightRowRef = useRef<HTMLTableRowElement | null>(null);
  const [validationErrors, setValidationErrors] = useState<Map<string, ValidationError[]>>(new Map());
  // Tracks the raw string being typed so intermediate states like "3." aren't lost to parseFloat
  const [rawInputValues, setRawInputValues] = useState<Map<string, string>>(new Map());

  // Format period for display
  const formatPeriod = useCallback(
    (year: number, unit: number) => {
      if (updateCadence === 'weekly') {
        return formatIsoWeekFullLabel(year, unit);
      }
      if (updateCadence === 'quarterly') {
        const quarter = getQuarterFromMonth(unit);
        return `${QUARTERS.find((q) => q.value === quarter)?.label || 'Q?'} ${year}`;
      }
      return `${MONTHS[unit - 1]} ${year}`;
    },
    [updateCadence]
  );

  // Build combined rows from saved and pending values, including missing periods
  const rows: DataValueRow[] = useMemo(() => {
    // Use a consistent ID based on period to prevent focus loss when editing
    const makeRowId = (year: number, unit: number) => `period-${year}-${unit}`;

    // Helper to find saved value for a period
    const getSavedValue = (year: number, unit: number) => {
      return savedValues.find(
        (v) => v.period_year === year && getUnit(v, updateCadence) === unit && !deletedSavedValueIds.includes(v.id)
      );
    };

    // Helper to find pending value for a period
    const getPendingValue = (year: number, unit: number) => {
      return pendingValues.find((v) => v.period_year === year && getUnit(v, updateCadence) === unit);
    };

    // Collect all periods from saved and pending values that match this
    // card's current cadence shape - a card whose cadence was switched can
    // still have stale saved rows of the old shape (e.g. a week number and
    // a calendar month can coincide, week 6 vs. June), so only rows whose
    // populated field matches the current cadence are considered.
    const matchesCadence = (v: PeriodValue) =>
      updateCadence === 'weekly' ? v.period_week !== null : v.period_month !== null;

    const allPeriods = [
      ...savedValues
        .filter((v) => !deletedSavedValueIds.includes(v.id) && matchesCadence(v))
        .map((v) => ({ year: v.period_year, unit: getUnit(v, updateCadence) })),
      ...pendingValues.filter(matchesCadence).map((v) => ({ year: v.period_year, unit: getUnit(v, updateCadence) })),
      ...(highlightedPeriod && matchesCadence(highlightedPeriod)
        ? [{ year: highlightedPeriod.period_year, unit: getUnit(highlightedPeriod, updateCadence) }]
        : [])
    ];

    // If no data at all, show current period as empty
    if (allPeriods.length === 0) {
      const { year, unit } = currentPeriod(updateCadence);
      const period = fromUnit(year, unit, updateCadence);
      return [
        {
          id: makeRowId(year, unit),
          savedValueId: null,
          period_year: period.period_year,
          period_month: period.period_month,
          period_week: period.period_week,
          value: '',
          isPending: true
        }
      ];
    }

    // Find earliest and latest periods
    const sortedPeriods = [...allPeriods].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.unit - b.unit;
    });

    const earliest = sortedPeriods[0];
    const now = currentPeriod(updateCadence);

    // Latest is either the most recent data or current period, whichever is later
    const latestData = sortedPeriods[sortedPeriods.length - 1];
    const latest =
      latestData.year > now.year || (latestData.year === now.year && latestData.unit > now.unit) ? latestData : now;

    // Generate all periods from earliest to latest
    const result: DataValueRow[] = [];
    let year = earliest.year;
    let unit = earliest.unit;

    // Guard against a pathological range producing an unbounded loop.
    let iterations = 0;
    const MAX_ITERATIONS = 5000;

    while ((year < latest.year || (year === latest.year && unit <= latest.unit)) && iterations < MAX_ITERATIONS) {
      iterations++;
      const saved = getSavedValue(year, unit);
      const pending = getPendingValue(year, unit);
      const period = fromUnit(year, unit, updateCadence);

      if (pending !== undefined) {
        // Pending value takes precedence
        result.push({
          id: makeRowId(year, unit),
          savedValueId: saved?.id || null,
          period_year: period.period_year,
          period_month: period.period_month,
          period_week: period.period_week,
          value: pending.value === null ? '' : String(pending.value),
          isPending: true
        });
      } else if (saved) {
        // Saved value
        result.push({
          id: makeRowId(year, unit),
          savedValueId: saved.id,
          period_year: period.period_year,
          period_month: period.period_month,
          period_week: period.period_week,
          value: String(saved.value),
          isPending: false
        });
      } else {
        // Missing period - show as empty row
        result.push({
          id: makeRowId(year, unit),
          savedValueId: null,
          period_year: period.period_year,
          period_month: period.period_month,
          period_week: period.period_week,
          value: '',
          isPending: false, // Not pending until user enters something
          isNew: true // Mark as a new/empty row
        });
      }

      const next = stepPeriod(year, unit, updateCadence, 1);
      year = next.year;
      unit = next.unit;
    }

    // Newest-first only in the card details pane (`fillHeight`), so "This period"
    // sits at the top. Classic modal keeps oldest → newest.
    if (fillHeight) {
      result.reverse();
    }
    return result;
  }, [savedValues, pendingValues, deletedSavedValueIds, updateCadence, highlightedPeriod, fillHeight]);

  // Don't depend on `rows`: pending edits rebuild it every keystroke and
  // would scroll "This period" back into view mid-edit.
  useEffect(() => {
    highlightRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [
    highlightedPeriod?.period_year,
    highlightedPeriod?.period_month,
    highlightedPeriod?.period_week,
    updateCadence,
    fillHeight
  ]);

  const newestFirst = fillHeight;

  // Calculate next period (after the latest) for new row
  const getNextPeriod = useCallback((): { year: number; unit: number } => {
    if (rows.length === 0) {
      return currentPeriod(updateCadence);
    }
    const mostRecent = newestFirst ? rows[0] : rows[rows.length - 1];
    const unit = getUnit(mostRecent, updateCadence);
    return stepPeriod(mostRecent.period_year, unit, updateCadence, 1);
  }, [rows, updateCadence, newestFirst]);

  // Calculate previous period (before the earliest) for new row
  const getPreviousPeriod = useCallback((): { year: number; unit: number } => {
    if (rows.length === 0) {
      return currentPeriod(updateCadence);
    }
    const earliest = newestFirst ? rows[rows.length - 1] : rows[0];
    const unit = getUnit(earliest, updateCadence);
    return stepPeriod(earliest.period_year, unit, updateCadence, -1);
  }, [rows, updateCadence, newestFirst]);

  // Add a new row at the end (next/later period)
  const handleAddLaterRow = useCallback(() => {
    const { year, unit } = getNextPeriod();
    const period = fromUnit(year, unit, updateCadence);

    // Check if period already exists
    const existsInPending = pendingValues.some((v) => v.period_year === year && getUnit(v, updateCadence) === unit);
    const existsInSaved = savedValues.some(
      (v) => v.period_year === year && getUnit(v, updateCadence) === unit && !deletedSavedValueIds.includes(v.id)
    );

    if (!existsInPending && !existsInSaved) {
      onPendingValuesChange([...pendingValues, { ...period, value: null }]);

      // Focus the new row after it's rendered
      setTimeout(() => {
        const newRowId = `period-${year}-${unit}`;
        const input = inputRefs.current.get(newRowId);
        if (input) {
          input.focus();
        }
      }, 50);
    }
  }, [getNextPeriod, pendingValues, savedValues, deletedSavedValueIds, updateCadence, onPendingValuesChange]);

  // Add a new row at the beginning (previous/earlier period)
  const handleAddEarlierRow = useCallback(() => {
    const { year, unit } = getPreviousPeriod();
    const period = fromUnit(year, unit, updateCadence);

    // Check if period already exists
    const existsInPending = pendingValues.some((v) => v.period_year === year && getUnit(v, updateCadence) === unit);
    const existsInSaved = savedValues.some(
      (v) => v.period_year === year && getUnit(v, updateCadence) === unit && !deletedSavedValueIds.includes(v.id)
    );

    if (!existsInPending && !existsInSaved) {
      onPendingValuesChange([...pendingValues, { ...period, value: null }]);

      // Focus the new row after it's rendered
      setTimeout(() => {
        const newRowId = `period-${year}-${unit}`;
        const input = inputRefs.current.get(newRowId);
        if (input) {
          input.focus();
        }
      }, 50);
    }
  }, [getPreviousPeriod, pendingValues, savedValues, deletedSavedValueIds, updateCadence, onPendingValuesChange]);

  // Handle value change
  const handleValueChange = useCallback(
    (row: DataValueRow, newValue: string) => {
      // Preserve the raw string so intermediate states like "3." aren't lost to parseFloat
      setRawInputValues((prev) => {
        const next = new Map(prev);
        next.set(row.id, newValue);
        return next;
      });

      // Validate input (only if not empty - empty is valid)
      const errors = newValue.trim()
        ? validateDataValueInput(newValue, dataValueType, undefined, levelCalculationMode)
        : [];
      const newValidationErrors = new Map(validationErrors);
      if (errors.length > 0) {
        newValidationErrors.set(row.id, errors);
      } else {
        newValidationErrors.delete(row.id);
      }
      setValidationErrors(newValidationErrors);

      // Don't update numeric state for incomplete decimal inputs (e.g. "3." or "-")
      const trimmed = newValue.trim();
      if (trimmed.endsWith('.') || trimmed === '-') return;

      // Update pending values - empty string becomes null, otherwise parse as number
      const numValue = trimmed === '' ? null : parseFloat(trimmed) || 0;
      const rowUnit = getUnit(row, updateCadence);
      const existingIndex = pendingValues.findIndex(
        (v) => v.period_year === row.period_year && getUnit(v, updateCadence) === rowUnit
      );

      if (existingIndex >= 0) {
        // Update existing pending value
        const updated = [...pendingValues];
        updated[existingIndex] = { ...updated[existingIndex], value: numValue };
        onPendingValuesChange(updated);
      } else {
        // Add new pending value (this means we're editing a saved value)
        onPendingValuesChange([
          ...pendingValues,
          {
            period_year: row.period_year,
            period_month: row.period_month,
            period_week: row.period_week,
            value: numValue
          }
        ]);
      }
    },
    [pendingValues, dataValueType, levelCalculationMode, validationErrors, updateCadence, onPendingValuesChange]
  );

  // For status cards, set the Yes/No/Empty state for a given row
  const handleStatusChange = useCallback(
    (row: DataValueRow, newValue: 0 | 1 | null) => {
      const rowUnit = getUnit(row, updateCadence);
      const existingIndex = pendingValues.findIndex(
        (v) => v.period_year === row.period_year && getUnit(v, updateCadence) === rowUnit
      );
      const savedValue = savedValues.find(
        (v) => v.period_year === row.period_year && getUnit(v, updateCadence) === rowUnit
      );

      if (newValue === null) {
        // Clearing the value. If there was a pending value, drop it.
        if (existingIndex >= 0) {
          const updated = [...pendingValues];
          updated.splice(existingIndex, 1);
          onPendingValuesChange(updated);
        }
        // If there was a saved value, mark it for deletion.
        if (savedValue && !deletedSavedValueIds.includes(savedValue.id)) {
          onDeleteSavedValue(savedValue.id);
        }
        return;
      }

      if (existingIndex >= 0) {
        const updated = [...pendingValues];
        updated[existingIndex] = { ...updated[existingIndex], value: newValue };
        onPendingValuesChange(updated);
      } else {
        onPendingValuesChange([
          ...pendingValues,
          {
            period_year: row.period_year,
            period_month: row.period_month,
            period_week: row.period_week,
            value: newValue
          }
        ]);
      }
    },
    [pendingValues, savedValues, deletedSavedValueIds, updateCadence, onPendingValuesChange, onDeleteSavedValue]
  );

  // Handle row deletion
  const handleDeleteRow = useCallback(
    (row: DataValueRow) => {
      const rowUnit = getUnit(row, updateCadence);
      // Remove from pending values if it's there
      if (row.isPending) {
        onPendingValuesChange(
          pendingValues.filter((v) => !(v.period_year === row.period_year && getUnit(v, updateCadence) === rowUnit))
        );
      }
      // If there's an underlying saved value, mark it for deletion
      if (row.savedValueId) {
        onDeleteSavedValue(row.savedValueId);
      }
    },
    [pendingValues, updateCadence, onPendingValuesChange, onDeleteSavedValue]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, row: DataValueRow) => {
      const input = e.currentTarget;

      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          // Move to the next row, or add a period at the end of the visual order
          if (rowIndex === rows.length - 1) {
            newestFirst ? handleAddEarlierRow() : handleAddLaterRow();
          } else {
            const nextRow = rows[rowIndex + 1];
            const nextInput = inputRefs.current.get(nextRow.id);
            if (nextInput) {
              nextInput.focus();
            }
          }
          break;

        case 'Tab':
          // Default tab behavior
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (rowIndex < rows.length - 1) {
            // Move to next row
            const nextRow = rows[rowIndex + 1];
            const nextInput = inputRefs.current.get(nextRow.id);
            if (nextInput) {
              nextInput.focus();
              nextInput.select();
            }
          } else {
            newestFirst ? handleAddEarlierRow() : handleAddLaterRow();
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (rowIndex > 0) {
            // Move to previous row
            const prevRow = rows[rowIndex - 1];
            const prevInput = inputRefs.current.get(prevRow.id);
            if (prevInput) {
              prevInput.focus();
              prevInput.select();
            }
          } else {
            newestFirst ? handleAddLaterRow() : handleAddEarlierRow();
          }
          break;

        case 'Escape':
          input.blur();
          break;

        case 'Delete':
          // Delete key removes the entire row only if the value field is empty
          // Otherwise, let default behavior work (forward-delete characters)
          if (input.value === '') {
            e.preventDefault();
            // Remember adjacent row to focus after deletion
            const adjacentRowId = rowIndex > 0 ? rows[rowIndex - 1]?.id : rows[rowIndex + 1]?.id;
            handleDeleteRow(row);
            // Focus adjacent row after deletion
            if (adjacentRowId) {
              setTimeout(() => {
                const adjacentInput = inputRefs.current.get(adjacentRowId);
                if (adjacentInput) {
                  adjacentInput.focus();
                }
              }, 50);
            }
          }
          // If value is not empty, default Delete behavior (forward-delete) applies
          break;
      }
    },
    [rows, handleAddLaterRow, handleAddEarlierRow, handleDeleteRow, newestFirst]
  );

  // Handle paste - supports pasting multiple values
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>, startRowIndex: number) => {
      const pastedText = e.clipboardData.getData('text');

      // Check if pasting multiple values (newline or tab separated)
      const values = pastedText
        .split(/[\n\t\r]+/)
        .map((v) => v.trim())
        .filter((v) => v !== '');

      if (values.length <= 1) {
        // Single value paste - let default behavior handle it
        return;
      }

      e.preventDefault();

      // Paste multiple values starting from current row
      const newPendingValues = [...pendingValues];
      let currentRowIndex = startRowIndex;
      let cursor: { year: number; unit: number } | null = null;

      for (const value of values) {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) continue;

        if (currentRowIndex < rows.length) {
          // Update existing row
          const row = rows[currentRowIndex];
          const rowUnit = getUnit(row, updateCadence);
          const existingPendingIndex = newPendingValues.findIndex(
            (v) => v.period_year === row.period_year && getUnit(v, updateCadence) === rowUnit
          );

          if (existingPendingIndex >= 0) {
            newPendingValues[existingPendingIndex] = { ...newPendingValues[existingPendingIndex], value: numValue };
          } else {
            newPendingValues.push({
              period_year: row.period_year,
              period_month: row.period_month,
              period_week: row.period_week,
              value: numValue
            });
          }
          cursor = { year: row.period_year, unit: rowUnit };
        } else {
          // Need to add new rows, continuing from the last row we touched
          if (!cursor) {
            const last = rows[rows.length - 1];
            cursor = last
              ? { year: last.period_year, unit: getUnit(last, updateCadence) }
              : currentPeriod(updateCadence);
          }
          // Visual order is newest-first in the details pane, so overflow continues backward in time.
          cursor = stepPeriod(cursor.year, cursor.unit, updateCadence, newestFirst ? -1 : 1);
          const period = fromUnit(cursor.year, cursor.unit, updateCadence);
          newPendingValues.push({ ...period, value: numValue });
        }
        currentRowIndex++;
      }

      onPendingValuesChange(newPendingValues);
    },
    [rows, pendingValues, updateCadence, onPendingValuesChange, newestFirst]
  );

  // Get display value for a row (from pending if available, otherwise from saved)
  const getDisplayValue = useCallback(
    (row: DataValueRow): string => {
      const rowUnit = getUnit(row, updateCadence);
      const pendingValue = pendingValues.find(
        (v) => v.period_year === row.period_year && getUnit(v, updateCadence) === rowUnit
      );
      if (pendingValue !== undefined) {
        // Return empty string for null values
        return pendingValue.value === null ? '' : String(pendingValue.value);
      }
      return row.value;
    },
    [pendingValues, updateCadence]
  );

  const periodNoun = updateCadence === 'quarterly' ? 'Quarter' : updateCadence === 'weekly' ? 'Week' : 'Month';

  if (rows.length === 0 && readOnly) {
    return (
      <div className="text-center py-8 text-content-subtle">
        <p className="text-sm">No data values recorded yet.</p>
      </div>
    );
  }

  return (
    <div className={fillHeight ? 'flex flex-col h-full min-h-0 gap-2' : 'space-y-2'}>
      {/* Add button above the table: later periods when newest-first, earlier when oldest-first */}
      {!readOnly && (
        <Button
          variant="outline"
          size="sm"
          icon={PlusIcon}
          onClick={newestFirst ? handleAddLaterRow : handleAddEarlierRow}
          className="w-full shrink-0"
        >
          {newestFirst ? `Add Later ${periodNoun}` : `Add Earlier ${periodNoun}`}
        </Button>
      )}

      <div
        className={`border border-border-default rounded-lg ${
          fillHeight ? 'flex-1 min-h-0 overflow-y-auto' : 'overflow-hidden'
        }`}
      >
        <table className="w-full text-sm">
          <thead className={fillHeight ? 'sticky top-0 z-10 bg-surface-subtle' : undefined}>
            <tr className="bg-surface-subtle border-b border-border-default">
              <th className="text-left px-3 py-2 font-semibold text-content-subtle w-64">Period</th>
              <th className="text-right px-3 py-2 font-semibold text-content-subtle">Value</th>
              {!readOnly && <th className="w-10"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const hasError = validationErrors.has(row.id);
              const errors = validationErrors.get(row.id) || [];
              const displayValue = getDisplayValue(row);
              const isEmpty = row.isNew && !row.isPending && displayValue === '';
              const hasData = displayValue !== '' || row.isPending || row.savedValueId;
              const rowUnit = getUnit(row, updateCadence);
              const isThisPeriod = highlightedPeriod != null && isSamePeriod(row, highlightedPeriod, updateCadence);

              return (
                <tr
                  key={row.id}
                  ref={isThisPeriod ? highlightRowRef : undefined}
                  aria-current={isThisPeriod ? 'date' : undefined}
                  className={`border-b border-border-default last:border-b-0 ${
                    hasError
                      ? 'bg-red-50/50'
                      : isThisPeriod
                        ? 'bg-surface-hover'
                        : row.isPending
                          ? 'bg-amber-50/50'
                          : isEmpty
                            ? 'bg-slate-50/50'
                            : 'bg-white'
                  }`}
                >
                  {/* Period column - read only */}
                  <td
                    className={`px-3 py-1.5 font-medium ${isEmpty ? 'text-content-subtlest' : 'text-content-subtle'}`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {formatPeriod(row.period_year, rowUnit)}
                      {isEmpty && (
                        <span title="Missing data" className="inline-flex" aria-label="Missing data">
                          <WarningIcon size={14} weight="fill" className="text-amber-500 shrink-0" aria-hidden />
                        </span>
                      )}
                      {row.isPending && displayValue !== '' && (
                        <span className="text-xs text-amber-600 font-normal">(unsaved)</span>
                      )}
                    </span>
                  </td>

                  {/* Value column - editable */}
                  <td className="px-1 py-1">
                    {isStatusCard ? (
                      <StatusToggle
                        displayValue={displayValue}
                        disabled={readOnly}
                        onChange={(v) => handleStatusChange(row, v)}
                      />
                    ) : readOnly ? (
                      <div className="text-right px-2 py-1 text-content-default">{displayValue}</div>
                    ) : (
                      <input
                        ref={(el) => {
                          if (el) {
                            inputRefs.current.set(row.id, el);
                          } else {
                            inputRefs.current.delete(row.id);
                          }
                        }}
                        type="text"
                        inputMode="decimal"
                        value={rawInputValues.get(row.id) ?? displayValue}
                        onChange={(e) => handleValueChange(row, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, row)}
                        onPaste={(e) => handlePaste(e, index)}
                        onBlur={() => {
                          setRawInputValues((prev) => {
                            const next = new Map(prev);
                            next.delete(row.id);
                            return next;
                          });
                        }}
                        placeholder={
                          dataValueType === 'percentage' && !levelCalculationMode?.startsWith('deviation')
                            ? '0-100'
                            : 'Enter value'
                        }
                        className={`w-full text-right px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          hasError
                            ? 'border-red-300 bg-red-50 text-red-900'
                            : 'border-border-default bg-white hover:border-border-hover'
                        }`}
                        aria-invalid={hasError}
                        aria-describedby={hasError ? `error-${row.id}` : undefined}
                      />
                    )}
                    {hasError && (
                      <div id={`error-${row.id}`} className="flex items-center gap-1 text-xs text-red-600 mt-1 px-2">
                        <WarningIcon size={12} />
                        {errors[0]?.message}
                      </div>
                    )}
                  </td>

                  {/* Delete button - only show if there's data to delete */}
                  {!readOnly && (
                    <td className="px-2 py-1">
                      {hasData && (
                        <button
                          type="button"
                          onClick={() => handleDeleteRow(row)}
                          className="p-1 text-content-subtlest hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          aria-label={`Delete ${formatPeriod(row.period_year, rowUnit)}`}
                        >
                          <TrashIcon size={16} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add button below the table: earlier periods when newest-first, later when oldest-first */}
      {!readOnly && (
        <Button
          variant="outline"
          size="sm"
          icon={PlusIcon}
          onClick={newestFirst ? handleAddEarlierRow : handleAddLaterRow}
          className="w-full shrink-0"
        >
          {newestFirst ? `Add Earlier ${periodNoun}` : `Add Later ${periodNoun}`}
        </Button>
      )}
    </div>
  );
}

/**
 * Yes / No toggle used for Status Cards.
 * displayValue is the stringified numeric value ("1", "0", or "").
 * Clearing the row is handled by the row's delete button, not this toggle.
 */
interface StatusToggleProps {
  displayValue: string;
  disabled?: boolean;
  onChange: (value: 0 | 1) => void;
}

function StatusToggle({ displayValue, disabled = false, onChange }: StatusToggleProps) {
  const current: 0 | 1 | null = displayValue === '1' ? 1 : displayValue === '0' ? 0 : null;

  const baseClasses = 'px-3 py-1 text-sm font-medium rounded transition-colors border';
  const selectedClasses = (active: boolean, tint: 'yes' | 'no') => {
    if (!active) return `${baseClasses} border-border-default bg-white text-content-subtle hover:bg-surface-subtle`;
    if (tint === 'yes') return `${baseClasses} border-green-500 bg-green-50 text-green-700`;
    return `${baseClasses} border-red-500 bg-red-50 text-red-700`;
  };

  return (
    <div className="flex justify-end gap-1 px-2 py-1">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(1)}
        className={selectedClasses(current === 1, 'yes')}
        aria-pressed={current === 1}
      >
        Yes
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(0)}
        className={selectedClasses(current === 0, 'no')}
        aria-pressed={current === 0}
      >
        No
      </button>
    </div>
  );
}
